/**
 * Aureliseé, gedrag bij de kieslijsten in het mandje.
 *
 * Twee dingen, meer niet:
 * 1. Een ontbrekende stap in de vergeet-niet-tekst klapt zijn kieslijst open.
 * 2. Een product uit die lijst gaat in het mandje.
 *
 * Het bijwerken van het mandje doen we niet zelf. We vragen de verse secties mee
 * op in hetzelfde verzoek en melden dat via de eigen gebeurtenis van het thema.
 * Vessel werkt daarna zelf het mandje bij met morph, dus de lade blijft open
 * staan en er springt niets.
 *
 * Zie snippets/au-bouwlog.liquid.
 */

import { CartAddEvent } from '@theme/events';

/** @returns {string} De secties die het mandje tonen, komma-gescheiden. */
function cartSections() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  return [...new Set(ids)].join(',');
}

/**
 * Klapt de kieslijst van een stap open en de rest dicht. Nog een klik op dezelfde
 * stap klapt hem weer dicht.
 * @param {string} step
 * @param {ParentNode} [root]
 */
function togglePicklist(step, root = document) {
  /* alleen de lijsten bij deze tekst: op de telefoon staat de vergeet-niet-tekst twee
     keer in de lade (onder de producten en, verborgen, in het onderblok) */
  for (const list of root.querySelectorAll('[data-au-pick]')) {
    const mine = list.getAttribute('data-au-pick') === step;
    list.hidden = mine ? !list.hidden : true;
    if (mine && !list.hidden) {
      /* telefoon: een stap klapt het hele mandje open; begin bovenaan */
      const scroller = list.closest('.au-nudge__row') && list.closest('cart-items-component');
      if (scroller) {
        scroller.scrollTop = 0;
      } else if (typeof list.scrollIntoView === 'function') {
        /* de lijst staat in het vaste onderblok; zorg dat hij in beeld komt */
        list.scrollIntoView({ block: 'nearest' });
      }
    }
  }
  /* de rij met het plusje (telefoon) toont een min zolang zijn lijst open is */
  for (const opener of root.querySelectorAll('[data-au-pick-open]')) {
    const list = root.querySelector(`[data-au-pick="${opener.getAttribute('data-au-pick-open')}"]`);
    opener.setAttribute('aria-expanded', String(Boolean(list && !list.hidden)));
  }
}

/**
 * Legt een product in het mandje.
 * @param {HTMLElement} button
 */
async function addToCart(button) {
  const id = button.getAttribute('data-au-add');
  if (!id || button.getAttribute('aria-busy') === 'true') return;

  button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: cartSections(),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'cart add failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((result) => result.json());

    document.dispatchEvent(
      new CartAddEvent(cart, 'au-cart-pick', {
        source: 'au-cart-pick',
        itemCount: cart.item_count,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('[au-cart]', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/**
 * Een open lijst boven een rij met een plusje (telefoon) gaat dicht bij een tik ernaast.
 * @param {Event} event
 */
function closeRowListsOutside(event) {
  const target = event.target;
  if (!(target instanceof Node)) return;
  for (const list of document.querySelectorAll('.au-nudge__row [data-au-pick]:not([hidden])')) {
    const row = list.closest('.au-nudge__row');
    if (row && !row.contains(target)) {
      list.hidden = true;
      row.querySelector('[data-au-pick-open]')?.setAttribute('aria-expanded', 'false');
    }
  }
}

/* ook op de lade zelf: Safari op de iPhone stuurt een tik op een plek zonder knop niet
   door naar document, wel naar een element met een eigen luisteraar */
document.querySelector('cart-drawer-component')?.addEventListener('click', closeRowListsOutside);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  closeRowListsOutside(event);

  const opener = target.closest('[data-au-pick-open]');
  if (opener) {
    event.preventDefault();
    togglePicklist(opener.getAttribute('data-au-pick-open') || '', opener.closest('.au-nudge') || document);
    return;
  }

  const adder = target.closest('[data-au-add]');
  if (adder instanceof HTMLElement) {
    event.preventDefault();
    addToCart(adder);
  }
});

const auCartDrawer = document.querySelector('cart-drawer-component');

/**
 * Telefoon: de kop van het mandje wordt bij scrollen doorzichtig en wazig, net als de
 * menubalk (Jesse, 14 oktober 2026). Het hele mandje scrolt (cart-items-component, zie
 * stylesheets.liquid); zodra het niet bovenaan staat, krijgt de dialoog au-cart-scrolled.
 * @param {Element} scroller
 */
function syncCartScrolled(scroller) {
  const dialog = scroller.closest('dialog');
  if (!dialog) return;
  /* het mandje begint precies onder de menubalk: meet waar die nu ophoudt (met of zonder
     aankondigingsbalk erboven, ook als die al weggescrold is) (Jesse, 16 oktober 2026) */
  const header = document.querySelector('#header-component');
  if (header && dialog.open) {
    const rect = header.getBoundingClientRect();
    dialog.style.setProperty('--au-cart-top', `${Math.max(0, Math.round(rect.bottom))}px`);
    header.style.setProperty('--au-cart-top', `${Math.max(0, Math.round(rect.bottom))}px`);
    /* en hoeveel er boven de menubalk staat (de aankondigingsbalk), voor de wazige laag */
    header.style.setProperty('--au-header-top', `${Math.max(0, Math.round(rect.top))}px`);
  }
  dialog.classList.toggle('au-cart-scrolled', scroller.scrollTop > 0);
  /* de balk met Check out: zweeft hij nog over de producten, of staat hij op zijn plek onder
     Total? Op zijn plek staat het merkteken direct eronder in beeld (au-ctas-docked); dan krijgt
     de balk geen eigen achtergrond meer, geen laag op laag (Jesse, 16 oktober 2026) */
  const end = scroller.querySelector('.au-ctas-end');
  if (end) {
    dialog.classList.toggle('au-ctas-docked', end.getBoundingClientRect().top <= scroller.getBoundingClientRect().bottom + 1);
  }
}

document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches('.cart-drawer__dialog cart-items-component')) {
      syncCartScrolled(target);
    }
  },
  { capture: true, passive: true }
);

/* het kruisje in het lege mandje (snippets/header-actions.liquid, Jesse 17 oktober 2026): even hoog
   als Your cart is empty en precies onder het mandje-icoon in de kop. Gemeten ten opzichte van het
   vlak waarin het kruisje staat (offsetParent), ook na het opkomen van het paneel. */
function auPlaceCartX() {
  if (!auCartDrawer) return;
  const dialog = auCartDrawer.querySelector('dialog.cart-drawer__dialog');
  const button = dialog && dialog.querySelector('.au-cart-x');
  const heading = dialog && dialog.querySelector('.cart-drawer__heading--empty');
  const icon = auCartDrawer.querySelector('.header-actions__action');
  if (!dialog || !dialog.open || !button || !heading || !icon) return;
  const frame = (button.offsetParent || dialog).getBoundingClientRect();
  const head = heading.getBoundingClientRect();
  const iconBox = icon.getBoundingClientRect();
  const size = button.offsetWidth || 36;
  const top = head.top + head.height / 2 - frame.top - size / 2;
  const right = frame.right - (iconBox.left + iconBox.width / 2) - size / 2;
  button.style.setProperty('--au-cart-x-top', `${Math.round(top)}px`);
  button.style.setProperty('--au-cart-x-right', `${Math.max(0, Math.round(right))}px`);
}

/* het inlogvak van het account (Shopify's shopify-account in de kop, als au_account_direct uit staat)
   komt een stukje onder de menubalk (stylesheets.liquid, au_popover_gap). Bij het openen meten waar de
   balk nu ophoudt, ook met een aankondigingsbalk erboven (Jesse, 18 oktober 2026). */
document.addEventListener(
  'click',
  (event) => {
    const account = event.composedPath().find((el) => el instanceof Element && el.localName === 'shopify-account');
    const header = document.querySelector('#header-component');
    if (!account || !header || !header.contains(account)) return;
    account.style.setProperty('--au-account-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
  },
  { capture: true }
);

/* staat de pagina niet bovenaan, dan au-scrolled op html: de menubalk op de productpagina is bovenaan
   wit en pas bij het scrollen wazig (stylesheets.liquid, au_product_header_white; Jesse, 18 oktober 2026) */
(() => {
  const root = document.documentElement;
  const set = () => root.classList.toggle('au-scrolled', window.scrollY > 0);
  set();
  window.addEventListener('scroll', set, { passive: true });
})();

/* de onderkant van de vaste menubalk als --au-sticky-top op html, voor wat eronder moet plakken
   (de foto en de miniaturen op de productpagina; Jesse, 17 oktober 2026). Plakt de kop niet
   (geen sticky), dan 0. */
(() => {
  const header = document.querySelector('#header-component');
  if (!header) return;
  const section = header.closest('.shopify-section') || header.parentElement;
  const set = () => {
    const style = section ? getComputedStyle(section) : null;
    const sticky = Boolean(style && style.position === 'sticky');
    const top = sticky ? parseFloat(style.top) || 0 : 0;
    const value = sticky ? Math.max(0, Math.round(header.getBoundingClientRect().height + top)) : 0;
    document.documentElement.style.setProperty('--au-sticky-top', `${value}px`);
  };
  set();
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
  window.addEventListener('resize', set, { passive: true });
})();

/* staat het mandje open en tik je op de menustreepjes, dan gaat het mandje dicht en daarna gaat
   het menu echt open (Jesse, 17 oktober 2026; au_cart_menu_through). Het mandje is een modale
   dialoog: de tik valt op zijn waas en niet op de streepjes, dus we kijken of hij binnen de
   streepjes lag en klikken ze zodra het mandje dicht is. */
let auMenuAfterCart = false;
document.addEventListener(
  'click',
  (event) => {
    if (!auCartDrawer || event.detail === 0) return;
    if (getComputedStyle(document.documentElement).getPropertyValue('--au-cart-menu-through').trim() !== '1') return;
    const dialog = auCartDrawer.querySelector('dialog.cart-drawer__dialog');
    if (!dialog || !dialog.open) return;
    const burger = document.querySelector('header-drawer .header__icon--menu');
    if (!burger) return;
    const box = burger.getBoundingClientRect();
    if (box.width === 0) return;
    const x = event.clientX;
    const y = event.clientY;
    if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) auMenuAfterCart = true;
  },
  true
);

/* na een nieuwe inhoud (bijv. een product erbij) of bij openen de stand opnieuw zetten */
if (auCartDrawer) {
  /* net na het sluiten zet de kop zijn onderlagen meteen terug in plaats van ze te laten krimpen:
     anders leek het of het menu mee dichtklapte (Jesse, 16 oktober 2026); zie stylesheets.liquid */
  let auCartWasOpen = false;
  new MutationObserver(() => {
    for (const scroller of auCartDrawer.querySelectorAll('.cart-drawer__dialog cart-items-component')) {
      syncCartScrolled(scroller);
    }
    const dialog = auCartDrawer.querySelector('dialog.cart-drawer__dialog');
    const isOpen = Boolean(dialog && dialog.open);
    if (auCartWasOpen && !isOpen) {
      const header = document.querySelector('#header-component');
      if (header) {
        header.classList.add('au-cart-just-closed');
        window.setTimeout(() => header.classList.remove('au-cart-just-closed'), 700);
      }
      if (auMenuAfterCart) {
        auMenuAfterCart = false;
        const burger = document.querySelector('header-drawer .header__icon--menu');
        if (burger) window.setTimeout(() => burger.click(), 0);
      }
    }
    if (isOpen && !auCartWasOpen) {
      auPlaceCartX();
      window.setTimeout(auPlaceCartX, 300);
    } else if (isOpen) {
      auPlaceCartX();
    }
    auCartWasOpen = isOpen;
  }).observe(auCartDrawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  window.addEventListener(
    'resize',
    () => {
      auPlaceCartX();
      for (const scroller of auCartDrawer.querySelectorAll('.cart-drawer__dialog cart-items-component')) {
        syncCartScrolled(scroller);
      }
    },
    { passive: true }
  );
}
